import { useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  projectsApi,
  type ImportProjectRow,
  type ImportProjectsResult,
} from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';

// ─── CSV parsing ─────────────────────────────────────────────────────────────

/**
 * SharePoint exports prepend a `ListSchema={...big json...}` block to the CSV.
 * Strip it (matching balanced braces) before parsing. Also tolerates a UTF-8
 * BOM at the start of the file.
 */
function stripListSchema(raw: string): string {
  let s = raw.replace(/^﻿/, '');
  if (!s.startsWith('ListSchema=')) return s;
  // Find the first '{' and scan until balanced.
  const start = s.indexOf('{');
  if (start < 0) return s;
  let depth = 0;
  for (let i = start; i < s.length; i++) {
    if (s[i] === '{') depth++;
    else if (s[i] === '}') { depth--; if (depth === 0) {
      // Skip CRLF / LF that follow the closing brace.
      return s.slice(i + 1).replace(/^[\r\n]+/, '');
    } }
  }
  return s;
}

/** Minimal RFC-4180 CSV parser supporting quoted fields and escaped quotes. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') { inQ = false; }
      else { field += c; }
    } else {
      if (c === '"') { inQ = true; }
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\r') { /* skip */ }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else { field += c; }
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

// ─── Column → DTO field mapping ──────────────────────────────────────────────
// CSV column headers from the SharePoint Projects list, mapped onto our
// ImportProjectRow keys. Any column not in this map is treated as a checklist
// item (its header becomes a checklist_items.code).

const COLUMN_MAP: Record<string, keyof ImportProjectRow> = {
  'Job Name':              'title',
  'JobNumber':             'jobNumber',
  'Division':              'division',
  'Client':                'client',
  'ProjectStatus':         'projectStatus',
  'MobilizationStatus':    'mobilizationStatus',
  'PrimaryPM':             'primaryPmEmail',
  'SecondaryPM':           'secondaryPmEmail',
  'Estimator':             'estimatorEmail',
  'SafetyManager':         'safetyManagerEmail',
  'Original Start Date':   'originalStartDate',
  'Current Start Date':    'currentStartDate',
  'Estimated Completion':  'estimatedCompletion',
  'EstimatorHandoffDate':  'estimatorHandoffDate',
  'FieldLeaderHandoffDate':'fieldLeaderHandoffDate',
  'Original Duration':     'originalDuration',
  'Current Duration':      'currentDuration',
  'BidPeakCrew':           'bidPeakCrew',
  'BidWeeklyHours':        'bidWeeklyHours',
  'isArchived':            'isArchived',
  'TravelJob':             'travelJob',
  'PerDiem':               'perDiem',
  'PerDiemDays':           'perDiemDays',
  'JobAddress':            'jobAddress',
  'SiteContactName':       'siteContactName',
  'SiteContactPhone':      'siteContactPhone',
  'SiteContactEmail':      'siteContactEmail',
  'ClinicName':            'clinicName',
  'ClinicAddress':         'clinicAddress',
  'ClinicPhone':           'clinicPhone',
  'DelayNotes':            'delayNotes',
  'EstimatorNotes':        'estimatorNotes',
  'PMNotes':               'pmNotes',
  'TravelDistanceNote':    'travelDistanceNote',
  'JobFolderUrl':          'jobFolderUrl',
};

// Columns that are auto-derived in the system and should never be imported.
const IGNORED_COLUMNS = new Set([
  'DaysDelayed',
  // Retired in #395. It must be IGNORED rather than merely unmapped: anything
  // not in COLUMN_MAP that carries a value is treated as a checklist column, so
  // dropping it from the map alone would put it in the checklist preview and
  // make the API warn 'Unknown checklist item: Field Leader' on every existing
  // SharePoint export.
  'Field Leader',
  'ChecklistCompletePercent',
  'JobOnManpowerSchedule',     // checklist item, auto-derived from manpower
]);

interface ParsedFile {
  rows: ImportProjectRow[];
  headers: string[];
}

function buildImportRows(headers: string[], dataRows: string[][]): ImportProjectRow[] {
  return dataRows
    .filter((r) => r.some((c) => c.trim() !== ''))    // drop fully-blank rows
    .map((r) => {
      const obj: ImportProjectRow = { jobNumber: '' };
      const checklist: Record<string, string> = {};
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i].trim();
        const value  = (r[i] ?? '').trim();
        if (!header || IGNORED_COLUMNS.has(header)) continue;

        const mapped = COLUMN_MAP[header];
        if (mapped) {
          (obj as unknown as Record<string, string>)[mapped] = value;
        } else if (value !== '') {
          // Anything not in COLUMN_MAP that has a value is treated as a
          // checklist column. Empty checklist values are skipped (= preserve).
          checklist[header] = value;
        }
      }
      if (Object.keys(checklist).length > 0) obj.checklist = checklist;
      return obj;
    })
    .filter((r) => r.jobNumber.trim() !== '');         // must have a job#
}

// ─── Modal component ─────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
  onImported: () => void;
}

export default function ProjectsImportModal({ onClose, onImported }: Props) {
  const [parsed, setParsed]   = useState<ParsedFile | null>(null);
  const [fileName, setFileName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });

  const importMutation = useMutation({
    mutationFn: (rows: ImportProjectRow[]) => projectsApi.bulkImport(rows),
    onSuccess: () => { onImported(); },
  });

  function handleFile(file: File) {
    setFileName(file.name);
    setParseError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const raw = e.target?.result as string;
        const csv = stripListSchema(raw);
        const rows = parseCsv(csv);
        if (rows.length < 2) {
          setParseError('No data rows found in the file.');
          setParsed(null);
          return;
        }
        const headers = rows[0];
        const dataRows = rows.slice(1);
        const importRows = buildImportRows(headers, dataRows);
        if (importRows.length === 0) {
          setParseError('No rows with a JobNumber were found.');
          setParsed(null);
          return;
        }
        setParsed({ rows: importRows, headers });
      } catch (err) {
        setParseError(`Could not parse the file: ${(err as Error).message}`);
        setParsed(null);
      }
    };
    reader.onerror = () => setParseError('Could not read the file.');
    reader.readAsText(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }

  // Preview metrics for the summary line.
  const stats = useMemo(() => {
    if (!parsed) return null;
    const checklistKeys = new Set<string>();
    let withChecklist = 0, withPm = 0;
    for (const r of parsed.rows) {
      if (r.checklist && Object.keys(r.checklist).length > 0) {
        withChecklist++;
        Object.keys(r.checklist).forEach((k) => checklistKeys.add(k));
      }
      if (r.primaryPmEmail || r.estimatorEmail || r.safetyManagerEmail) withPm++;
    }
    return { total: parsed.rows.length, withChecklist, withPm, checklistFields: checklistKeys.size };
  }, [parsed]);

  // Sample of fields filled per row (capped) — drives the preview rows.
  const previewRows = useMemo(() => parsed?.rows.slice(0, 200) ?? [], [parsed]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} style={{ background: 'var(--ew-white, #fff)', borderRadius: 'var(--ew-r-lg)', width: '100%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', fontFamily: 'var(--ew-font)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Import Projects from CSV</h3>
          <span style={{ marginLeft: 12, fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 'var(--ew-r-sm)', padding: '2px 6px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Admin only
          </span>
          <button onClick={onClose} aria-label="Close import modal" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--gray)', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? 'var(--ew, #2d8c54)' : 'var(--border)'}`,
              borderRadius: 'var(--ew-r-lg)',
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragging ? '#f0fdf4' : '#f8fafc',
              marginBottom: 20,
              transition: 'border-color 120ms, background 120ms',
            }}
          >
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            <div style={{ fontSize: 28, marginBottom: 6 }}>📋</div>
            {fileName ? (
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ew, #2d8c54)' }}>{fileName}</div>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Drop the EW Projects CSV here</div>
                <div style={{ fontSize: 12, color: 'var(--gray)' }}>or click to browse · .csv</div>
                <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 8 }}>
                  Handles the SharePoint <code>ListSchema=…</code> prefix automatically.
                </div>
              </>
            )}
          </div>

          {parseError && (
            <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--ew-r-md)', fontSize: 13, color: '#b91c1c', marginBottom: 12 }}>
              {parseError}
            </div>
          )}

          {parsed && stats && (
            <>
              {/* Summary chips */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
                <Chip color="#065f46" bg="#d1fae5" border="#a7f3d0">
                  ✓ {stats.total} row{stats.total === 1 ? '' : 's'} parsed
                </Chip>
                <Chip color="#1e40af" bg="#dbeafe" border="#bfdbfe">
                  {stats.withPm} with PM / Estimator / Safety
                </Chip>
                <Chip color="#92400e" bg="#fef3c7" border="#fde68a">
                  {stats.withChecklist} with checklist data ({stats.checklistFields} columns)
                </Chip>
              </div>

              {/* Preview table — small read of each row's most useful fields */}
              <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', maxHeight: 360 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--ew-font)' }}>
                  <thead>
                    <tr style={{ background: 'var(--ew-dark, #111)', color: '#fff' }}>
                      <Th>Job #</Th>
                      <Th>Title</Th>
                      <Th>Division</Th>
                      <Th>Status</Th>
                      <Th>Mob</Th>
                      <Th>Primary PM</Th>
                      <Th>Checklist</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={r.jobNumber + '-' + i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <Td>{r.jobNumber}</Td>
                        <Td title={r.title}>{truncate(r.title, 40)}</Td>
                        <Td>{r.division ?? '—'}</Td>
                        <Td>{r.projectStatus ?? '—'}</Td>
                        <Td>{r.mobilizationStatus ?? '—'}</Td>
                        <Td title={r.primaryPmEmail}>{truncate(r.primaryPmEmail, 24)}</Td>
                        <Td>{r.checklist ? Object.keys(r.checklist).length + ' set' : '—'}</Td>
                      </tr>
                    ))}
                    {parsed.rows.length > previewRows.length && (
                      <tr><Td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray)', fontStyle: 'italic' }}>
                        … and {parsed.rows.length - previewRows.length} more rows (not previewed but will import)
                      </Td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray)' }}>
                Existing projects are matched by Job #; any row whose Job # is not in the database will be created.
                Empty cells preserve the existing value. Status, Mob, Division, and PM/Estimator/Safety emails are resolved server-side — unknown values are skipped with a warning.
              </div>
            </>
          )}

          {/* Mutation status */}
          {importMutation.isError && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--ew-r-md)', fontSize: 13, color: '#b91c1c' }}>
              Import failed: {(importMutation.error as Error).message}
            </div>
          )}
          {importMutation.isSuccess && importMutation.data && (
            <ImportSummary result={importMutation.data} />
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 24px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} style={btnSecondary}>
            {importMutation.isSuccess ? 'Done' : 'Cancel'}
          </button>
          {!importMutation.isSuccess && (
            <button
              disabled={!parsed || importMutation.isPending}
              onClick={() => parsed && importMutation.mutate(parsed.rows)}
              style={{ ...btnPrimary, opacity: !parsed || importMutation.isPending ? 0.55 : 1 }}
            >
              {importMutation.isPending
                ? 'Importing…'
                : parsed ? `Import ${parsed.rows.length} projects` : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Small subcomponents ─────────────────────────────────────────────────────

function ImportSummary({ result }: { result: ImportProjectsResult }) {
  return (
    <div style={{ marginTop: 12, padding: '12px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 'var(--ew-r-md)', fontSize: 13, color: '#065f46' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        ✓ Imported: {result.updated} updated · {result.created} created
        {result.skipped > 0 && ` · ${result.skipped} skipped`}
      </div>
      {result.warnings.length > 0 && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#92400e' }}>
            {result.warnings.length} warning{result.warnings.length === 1 ? '' : 's'}
          </summary>
          <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, color: '#92400e', fontSize: 12 }}>
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

function Chip({ color, bg, border, children }: { color: string; bg: string; border: string; children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 9px', fontSize: 11, fontWeight: 600,
      border: `1px solid ${border}`, borderRadius: 'var(--ew-r-pill)', whiteSpace: 'nowrap',
      color, background: bg,
    }}>{children}</span>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={{ padding: '7px 8px', fontSize: 11, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.07)' }}>{children}</th>;
}

function Td({ children, colSpan, style, title }: { children?: React.ReactNode; colSpan?: number; style?: React.CSSProperties; title?: string }) {
  return <td colSpan={colSpan} title={title} style={{ padding: '5px 8px', borderBottom: '1px solid #eef2f5', borderRight: '1px solid #eef2f5', verticalAlign: 'top', ...style }}>{children}</td>;
}

function truncate(s: string | undefined, n: number): string {
  if (!s) return '—';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

const btnSecondary: React.CSSProperties = {
  fontSize: 12, color: 'var(--gray)', border: '1px solid var(--border)',
  borderRadius: 'var(--ew-r-md)', padding: '6px 14px', background: '#fff', cursor: 'pointer', fontWeight: 600,
};
const btnPrimary: React.CSSProperties = {
  fontSize: 12, color: '#fff', background: 'var(--ew, #2d8c54)', border: 'none',
  borderRadius: 'var(--ew-r-md)', padding: '6px 14px', cursor: 'pointer', fontWeight: 600,
};
